import React, { useState, useEffect, useRef } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Save, Upload, X } from 'lucide-react';

const token = () => localStorage.getItem('token');

const FONTS = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Helvetica Neue, Helvetica, sans-serif', label: 'Helvetica' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: '"Trebuchet MS", sans-serif', label: 'Trebuchet MS' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: '"Palatino Linotype", Palatino, serif', label: 'Palatino' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
];

export function buildSignatureHTML(sig) {
  if (!sig || !sig.name) return '<div style="color:#aaa;font-style:italic;font-size:12px;">İmza önizlemesi burada görünecek...</div>';
  const font = sig.font_family || 'Arial, sans-serif';
  const lines = [];
  lines.push(`<div style="font-family:${font};font-size:13px;color:#333;border-top:2px solid #e5e7eb;margin-top:20px;padding-top:14px;">`);
  if (sig.logo_url) {
    lines.push(`<img src="${sig.logo_url}" style="max-height:52px;max-width:160px;margin-bottom:8px;display:block;" />`);
  }
  if (sig.name) {
    lines.push(`<strong style="font-size:14px;color:#111;display:block;">${sig.name}</strong>`);
  }
  const sub = [sig.title, sig.company].filter(Boolean).join(' | ');
  if (sub) lines.push(`<span style="color:#555;font-size:12px;">${sub}</span><br/>`);
  if (sig.phone) lines.push(`<span style="color:#666;font-size:12px;">Tel: ${sig.phone}</span><br/>`);
  if (sig.email) lines.push(`<span style="color:#666;font-size:12px;">E-posta: <a href="mailto:${sig.email}" style="color:#0d9488;">${sig.email}</a></span><br/>`);
  if (sig.website) {
    const url = sig.website.startsWith('http') ? sig.website : `https://${sig.website}`;
    lines.push(`<span style="color:#666;font-size:12px;">Web: <a href="${url}" style="color:#0d9488;">${sig.website}</a></span><br/>`);
  }
  const social = [];
  if (sig.linkedin) social.push(`<a href="${sig.linkedin}" style="color:#0a66c2;font-size:12px;font-weight:600;text-decoration:none;margin-right:10px;">LinkedIn</a>`);
  if (sig.instagram) social.push(`<a href="${sig.instagram}" style="color:#e1306c;font-size:12px;font-weight:600;text-decoration:none;">Instagram</a>`);
  if (social.length) lines.push(`<span>${social.join('')}</span><br/>`);
  if (sig.address) lines.push(`<span style="color:#888;font-size:11px;display:block;margin-top:4px;">${sig.address}</span>`);
  if (sig.extra_note) lines.push(`<span style="color:#888;font-size:11px;display:block;">${sig.extra_note}</span>`);
  lines.push('</div>');
  return lines.join('');
}

function buildSignaturePlainText(sig) {
  if (!sig || !sig.name) return '';
  const lines = [];
  if (sig.name) lines.push(sig.name);
  const sub = [sig.title, sig.company].filter(Boolean).join(' | ');
  if (sub) lines.push(sub);
  if (sig.phone) lines.push(`Tel: ${sig.phone}`);
  if (sig.email) lines.push(`E-posta: ${sig.email}`);
  if (sig.website) lines.push(`Web: ${sig.website}`);
  // Show clean handles instead of full URLs
  if (sig.linkedin) {
    const handle = sig.linkedin.replace(/\/$/, '').split('/').pop();
    lines.push(`LinkedIn: linkedin.com/in/${handle}`);
  }
  if (sig.instagram) {
    const handle = sig.instagram.replace(/\/$/, '').split('/').pop();
    lines.push(`Instagram: @${handle}`);
  }
  if (sig.address) lines.push(sig.address);
  if (sig.extra_note) lines.push(sig.extra_note);
  return lines.join('\n');
}

const EMPTY_SIG = { name: '', title: '', company: '', phone: '', email: '', website: '', linkedin: '', instagram: '', address: '', logo_url: '', extra_note: '', font_family: 'Arial, sans-serif' };

export default function EmailSignatureEditor() {
  const [sig, setSig] = useState(EMPTY_SIG);
  const [previewMode, setPreviewMode] = useState('html');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/email-signature`, { headers: { Authorization: `Bearer ${token()}` } })
      .then(res => setSig({ ...EMPTY_SIG, ...(res.data || {}) }))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-signature`, sig, { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('İmza kaydedildi');
    } catch { toast.error('Kaydedilemedi'); }
    finally { setSaving(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/logo`, formData, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' }
      });
      setSig(s => ({ ...s, logo_url: res.data.url }));
      toast.success('Logo yüklendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo yüklenemedi');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const field = (key, label, placeholder, type = 'text') => (
    <div className="space-y-1">
      <Label className="text-xs text-gray-500">{label}</Label>
      <Input value={sig[key] || ''} onChange={e => setSig(s => ({ ...s, [key]: e.target.value }))}
        placeholder={placeholder} type={type} className="h-8 text-sm" data-testid={`sig-${key}`} />
    </div>
  );

  return (
    <div className="space-y-4 max-w-4xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-700">İmza Bilgileri</p>
          </div>
          <div className="px-4 py-4 space-y-3">
            {/* Font selector */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Yazı Fontu</Label>
              <Select value={sig.font_family || 'Arial, sans-serif'} onValueChange={v => setSig(s => ({ ...s, font_family: v }))}>
                <SelectTrigger className="h-8 text-sm" data-testid="sig-font-family">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONTS.map(f => (
                    <SelectItem key={f.value} value={f.value}>
                      <span style={{ fontFamily: f.value }}>{f.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {field('name', 'Ad Soyad', 'Murat Bucak')}
              {field('title', 'Unvan', 'CEO / Co-Founder')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field('company', 'Şirket', 'Quattro Stand')}
              {field('phone', 'Telefon', '+90 532 315 00 03')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field('email', 'E-posta', 'ornek@sirket.com', 'email')}
              {field('website', 'Web Sitesi', 'www.sirket.com')}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {field('linkedin', 'LinkedIn URL', 'https://linkedin.com/in/...')}
              {field('instagram', 'Instagram URL', 'https://instagram.com/...')}
            </div>

            {/* Logo upload */}
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Logo</Label>
              <div className="flex gap-2 items-start">
                <div className="flex-1 space-y-1.5">
                  <div className="flex gap-2">
                    <Input
                      value={sig.logo_url || ''}
                      onChange={e => setSig(s => ({ ...s, logo_url: e.target.value }))}
                      placeholder="https://... veya dosya yükleyin →"
                      className="h-8 text-sm flex-1"
                      data-testid="sig-logo-url"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="h-8 gap-1.5 shrink-0"
                      data-testid="sig-logo-upload-btn"
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploading ? 'Yükleniyor...' : 'Yükle'}
                    </Button>
                  </div>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  {sig.logo_url && (
                    <div className="flex items-center gap-2">
                      <img src={sig.logo_url} alt="Logo" className="h-8 max-w-24 object-contain border border-gray-100 rounded" />
                      <button onClick={() => setSig(s => ({ ...s, logo_url: '' }))} className="text-red-400 hover:text-red-600">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Adres</Label>
              <Textarea value={sig.address || ''} onChange={e => setSig(s => ({ ...s, address: e.target.value }))}
                placeholder="Sokak, Şehir, Ülke" rows={2} className="text-sm" data-testid="sig-address" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Ek Not</Label>
              <Textarea value={sig.extra_note || ''} onChange={e => setSig(s => ({ ...s, extra_note: e.target.value }))}
                placeholder="UAE: +971... | Türkiye: +90..." rows={2} className="text-sm" data-testid="sig-extra-note" />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="border border-gray-200 rounded-xl overflow-hidden flex flex-col">
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Önizleme</p>
            <div className="flex gap-1">
              {['html', 'text'].map(mode => (
                <button key={mode} onClick={() => setPreviewMode(mode)}
                  className={`text-xs px-2.5 py-1 rounded-full transition-all ${previewMode === mode ? 'bg-teal-600 text-white' : 'bg-white border text-gray-500 hover:border-gray-300'}`}>
                  {mode === 'html' ? 'HTML Önizleme' : 'Düz Metin'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 p-4 bg-white min-h-48">
            {previewMode === 'html' ? (
              <div dangerouslySetInnerHTML={{ __html: buildSignatureHTML(sig) }} />
            ) : (
              <pre className="text-xs text-gray-600 whitespace-pre-wrap font-mono bg-gray-50 p-3 rounded-lg">
                {buildSignaturePlainText(sig) || 'İmza alanlarını doldurun...'}
              </pre>
            )}
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving} className="gap-2 bg-teal-600 hover:bg-teal-700" data-testid="save-signature-btn">
        <Save className="w-4 h-4" /> {saving ? 'Kaydediliyor...' : 'İmzayı Kaydet'}
      </Button>
    </div>
  );
}
